import React, { useState, useEffect, useRef, useCallback } from 'react';
import { appwriteConfig, client, databases } from '@/lib/appwrite/config';
import { sendLiveChatMessage } from '@/services/liveStreamService';
import { Query } from 'appwrite';
import { getViolationType, isUserBanned, recordUserViolation } from '@/lib/appwrite/userViolationAPI';
import { INITIAL_USER, useUserContext } from '@/context/AuthContext';
import { useSignOutAccount } from '@/lib/react-query/queries';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../ui/use-toast';
import { isBlockedMessage, violationTracker } from '@/lib/utils/blockedPatterns';
import { Send, ShoppingBag, HelpCircle, MessageCircle} from 'lucide-react';

interface LiveStreamChatProps {
  streamId: string;
  userId: string;
  userName: string;
  userRole: string;
  onNewMessage?: (message: ChatMessage) => void;
  onMessagesUpdate?: (messages: ChatMessage[]) => void;
}

interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  text: string;
  userRole: string;
  timestamp: string;
  priority?: 'normal' | 'question' | 'purchase';
}

const LiveStreamChat: React.FC<LiveStreamChatProps> = ({ 
  streamId, 
  userId, 
  userName, 
  userRole,
  onNewMessage,
  onMessagesUpdate
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [quickReplies] = useState([
    { icon: '👋', text: 'Hello!', type: 'greeting' },
    { icon: '💯', text: 'Amazing!', type: 'praise' },
    { icon: '❓', text: 'Price?', type: 'question' },
    { icon: '🛒', text: 'Available?', type: 'purchase' },
  ]);
  
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chatSubscriptionRef = useRef<(() => void) | null>(null);
  const componentMountedRef = useRef(false);
  
  const { setUser, setIsAuthenticated } = useUserContext();
  const { mutate: signOut } = useSignOutAccount();
  const navigate = useNavigate();
  const { toast } = useToast();

  const collectionId = appwriteConfig.liveStreamsChat;
  const databaseId = appwriteConfig.databaseId;

  const detectMessagePriority = (text: string): 'normal' | 'question' | 'purchase' => {
    const lowerText = text.toLowerCase();
    
    const purchaseKeywords = [
      'buy', 'purchase', 'order', 'cart', 'checkout', 'price', 'cost', 
      'how much', 'available', 'stock', 'quantity', 'shipping', 'delivery',
      'payment', 'pay', 'sell', 'discount', 'offer', 'deal', 'rupees', '₹',
      'rs', 'inr', 'money', 'expensive', 'cheap', 'afford', 'book', 'reserve'
    ];
    
    if (purchaseKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'purchase';
    }
    
    const questionKeywords = [
      '?', 'how', 'what', 'when', 'where', 'why', 'can', 'could', 'would', 
      'is', 'are', 'will', 'does', 'do', 'which', 'who', 'whose', 'help',
      'explain', 'tell', 'show', 'demo', 'review', 'opinion', 'think',
      'recommend', 'suggest', 'advice', 'guide', 'tutorial'
    ];
    
    if (questionKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'question';
    }
    
    return 'normal';
  };

  const getPriorityStyle = (priority: string, isCurrentUser: boolean) => {
    if (isCurrentUser) {
      return 'ml-auto bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg';
    }
    
    switch (priority) {
      case 'purchase':
        return 'mr-auto bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border border-green-200 shadow-md';
      case 'question':
        return 'mr-auto bg-gradient-to-r from-orange-50 to-amber-50 text-orange-800 border border-orange-200 shadow-md';
      default:
        return 'mr-auto bg-gray-50 text-gray-800 border border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'purchase':
        return <ShoppingBag size={12} className="text-green-600" />;
      case 'question':
        return <HelpCircle size={12} className="text-orange-600" />;
      default:
        return <MessageCircle size={12} className="text-gray-500" />;
    }
  };

  const updateMessages = useCallback((newMessages: ChatMessage[]) => {
    setMessages(newMessages);
    
    setTimeout(() => {
      if (onMessagesUpdate) {
        onMessagesUpdate(newMessages);
      }
    }, 0);
  }, [onMessagesUpdate]);

  useEffect(() => {
    componentMountedRef.current = true;
    
    return () => {
      componentMountedRef.current = false;
      
      if (chatSubscriptionRef.current) {
        chatSubscriptionRef.current();
        chatSubscriptionRef.current = null;
      }
    };
  }, [streamId]);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!componentMountedRef.current) return;
      
      try {
        const res = await databases.listDocuments(
          databaseId,
          collectionId,
          [
            Query.equal("streamId", streamId),
            Query.orderDesc("timestamp"),
            Query.limit(50),
          ]
        );

        const initialMsgs = res.documents
          .map((doc) => ({
            id: doc.$id,
            userId: doc.userId,
            userName: doc.userName,
            text: doc.text,
            timestamp: doc.timestamp,
            userRole: doc.userRole,
            priority: detectMessagePriority(doc.text)
          }))
          .reverse();

        if (componentMountedRef.current) {
          updateMessages(initialMsgs);
        }
      } catch (err) {
        console.error("Failed to load messages:", err);
      }
    };

    fetchMessages();
  }, [streamId]);

  useEffect(() => {
    if (!streamId || !componentMountedRef.current) return;

    if (chatSubscriptionRef.current) {
      chatSubscriptionRef.current();
    }

    try {
      const chatChannel = `databases.${databaseId}.collections.${collectionId}.documents`;
      
      const unsubscribe = client.subscribe(chatChannel, (response: any) => {
        if (!componentMountedRef.current) return;

        if (
          response.events.includes("databases.*.collections.*.documents.*.create") &&
          response.payload.streamId === streamId
        ) {
          const newMsg = {
            id: response.payload.$id,
            userId: response.payload.userId,
            userName: response.payload.userName,
            text: response.payload.text,
            timestamp: response.payload.timestamp,
            userRole: response.payload.userRole,
            priority: detectMessagePriority(response.payload.text)
          };

          setMessages(prev => {
            const updated = [...prev, newMsg];
            
            setTimeout(() => {
              if (onMessagesUpdate) {
                onMessagesUpdate(updated);
              }
            }, 0);

            return updated;
          });
          
          if (onNewMessage && newMsg.userId !== userId) {
            onNewMessage(newMsg);
          }
        }
      });

      chatSubscriptionRef.current = unsubscribe;

    } catch (error) {
      console.error('Failed to set up chat subscription:', error);
    }

    return () => {
      if (chatSubscriptionRef.current) {
        chatSubscriptionRef.current();
        chatSubscriptionRef.current = null;
      }
    };
  }, [streamId, onNewMessage, onMessagesUpdate, userId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || newMessage;
    if (!textToSend.trim()) return;

    try {
      const userIsBanned = await isUserBanned(userId);
      if (userIsBanned) {
        toast({
          title: "Account Suspended",
          description: "Your account has been suspended due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
          variant: "destructive"
        });
        signOut();
        setIsAuthenticated(false);
        setUser(INITIAL_USER);
        navigate("/sign-in?banned=true");
        return;
      }
      
      const blockResult = isBlockedMessage(textToSend);
      
      if (blockResult.isBlocked) {
        try {
          const violationType = getViolationType(blockResult.reason);
          const violationResult = await recordUserViolation(userId, {
            violationType,
            messageText: textToSend,
            severity: blockResult.severity
          });
          
          const { violation } = violationResult;
          
          let title = "Message Blocked";
          let description = blockResult.reason;
          
          switch (violation.violationStage) {
            case 'banned':
              title = "Account Suspended";
              description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
              break;
              
            case 'flagged':
              title = "Account Flagged";
              const flagDate = violation.flaggedUntil ? new Date(violation.flaggedUntil).toLocaleDateString('en-GB') : 'N/A';
              description = `${blockResult.reason}. Your account is now flagged until ${flagDate}. Further violations will result in account suspension. (Total violations: ${violation.violationCount})`;
              break;
              
            default:
              if (blockResult.severity === 'violation') {
                title = "Content Violation";
                description = `${blockResult.reason}. This violation has been recorded. (Total violations: ${violation.violationCount})`;
              } else {
                title = "Content Warning";
                description = `${blockResult.reason}. Please follow our community guidelines.`;
              }
          }
          
          toast({
            title,
            description,
            variant: "destructive"
          });
          
          if (blockResult.severity === 'violation') {
            setNewMessage('');
          }
          
          return;
          
        } catch (dbError) {
          console.error('Error recording violation to database:', dbError);
          
          const userStatus = violationTracker.recordViolation(userId, blockResult.severity);
          
          let title = "Message Blocked";
          let description = blockResult.reason;
          
          if (userStatus.isBanned) {
            title = "Account Suspended";
            description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
          } else if (userStatus.isFlagged) {
            title = "Account Flagged";
            description = `${blockResult.reason}. Your account is now flagged. Further violations will result in account suspension. (Warnings: ${userStatus.warnings}, Violations: ${userStatus.violations})`;
          } else if (blockResult.severity === 'warning') {
            description = `${blockResult.reason}. This is a warning - please follow our community guidelines.`;
          }
          
          toast({
            title,
            description,
            variant: "destructive"
          });
          
          if (blockResult.severity === 'violation') {
            setNewMessage('');
          }
          
          return;
        }
      }

      await sendLiveChatMessage({
        streamId,
        userId,
        userName,
        userRole,
        text: textToSend,
      });

      setNewMessage('');
      setIsTyping(false);
      inputRef.current?.focus();
      
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Message Failed",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleQuickReply = (reply: { text: string; type: string }) => {
    handleSendMessage(reply.text);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 text-white">
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400">
            <MessageCircle size={32} className="mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Be the first to say something!</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isCurrentUser = msg.userId === userId;
            const priorityStyle = getPriorityStyle(msg.priority || 'normal', isCurrentUser);
            
            return (
              <div key={`${msg.id}-${index}`} className="flex flex-col">
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm relative group ${priorityStyle} animate-in slide-in-from-left-2 duration-200`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      {!isCurrentUser && getPriorityIcon(msg.priority || 'normal')}
                      <span className="font-semibold text-xs">
                        {isCurrentUser ? 'You' : msg.userName}
                      </span>
                      {msg.userRole === 'seller' && (
                        <span className="bg-rose-500 text-white text-xs px-2 py-0.5 rounded-full">
                          Host
                        </span>
                      )}
                    </div>
                    <span className="text-xs opacity-70 group-hover:opacity-100 transition-opacity">
                      {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                  
                  <div className="leading-relaxed">
                    {msg.text}
                  </div>
                  
                  {!isCurrentUser && msg.priority !== 'normal' && (
                    <div className="absolute -top-1 -right-1">
                      <div className={`w-3 h-3 rounded-full ${
                        msg.priority === 'purchase' ? 'bg-green-500' : 'bg-orange-500'
                      } animate-pulse`}></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="px-4 py-2 border-t border-gray-700">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {quickReplies.map((reply, index) => (
            <button
              key={index}
              onClick={() => handleQuickReply(reply)}
              className="flex-shrink-0 flex items-center space-x-1 bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-full text-xs transition-all border border-gray-600 hover:border-gray-500 hover:scale-105 active:scale-95"
            >
              <span>{reply.icon}</span>
              <span>{reply.text}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 border-t border-gray-700 bg-gray-800/50">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                setIsTyping(e.target.value.length > 0);
              }}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-full px-4 py-3 pr-20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Type a message..."
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              maxLength={200}
              disabled={!componentMountedRef.current}
            />
            
            <div className={`absolute right-12 top-1/2 transform -translate-y-1/2 text-xs transition-colors ${
              newMessage.length > 180 ? 'text-rose-400' : 
              newMessage.length > 150 ? 'text-yellow-400' : 'text-gray-400'
            }`}>
              {newMessage.length}/200
            </div>
          </div>
          
          <button 
            onClick={() => handleSendMessage()}
            disabled={!newMessage.trim() || !componentMountedRef.current}
            className={`p-3 rounded-full transition-all transform ${
              newMessage.trim() && componentMountedRef.current
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95' 
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Send 
              size={16} 
              className={`transition-transform ${
                newMessage.trim() ? 'transform rotate-45' : ''
              }`} 
            />
          </button>
        </div>
        
        {isTyping && (
          <div className="mt-2 text-xs text-gray-400 flex items-center space-x-2 animate-in slide-in-from-bottom-2 duration-200">
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
              <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>Typing...</span>
            <div className="flex-1"></div>
            <span className="text-gray-500">Press Enter to send</span>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              componentMountedRef.current ? 'bg-green-400 animate-pulse' : 'bg-rose-400'
            }`}></div>
            <span className="text-gray-400">
              {componentMountedRef.current ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          
          {messages.length > 0 && (
            <span className="text-gray-500">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveStreamChat;